// Exercise-set player — `/courses/:slug/quizzes/:quizId` (public) and the
// dashboard equivalent. Shows an intro card, then runs the Duolingo-style
// ExerciseRunner, then a result screen. The run is generated + graded on the
// client; the resulting score is submitted so progress records it. See
// COURSES-SPEC §3.

import { useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { SiteHeader, PlatformFooter } from "../platform/PlatformChrome";
import { useQuizForLearner, useSubmitQuizAttempt } from "../../lib/courses";
import { useMe } from "../../lib/auth";
import type { PublicQuizForLearner, QuizResult } from "@vedaunion/shared";
import { generateExercises } from "./exercise/engine";
import type { GeneratedExercise } from "./exercise/types";
import { ExerciseRunner } from "./exercise/ExerciseRunner";

type Phase = "intro" | "running" | "result";

export default function QuizPlayer({ surface }: { surface: "public" | "dashboard" }) {
  const { slug, quizId: quizIdParam } = useParams<{ slug: string; quizId: string }>();
  const quizId = quizIdParam ? Number(quizIdParam) : undefined;

  const body =
    !slug || !quizId || Number.isNaN(quizId) ? (
      <div className="container-edit max-w-[760px]">
        <p className="font-grotesk text-[0.9rem] text-[var(--color-ink-mute)]">No quiz specified.</p>
      </div>
    ) : (
      <QuizBody slug={slug} quizId={quizId} />
    );

  if (surface === "public") {
    return (
      <>
        <SiteHeader />
        <main className="pt-24 sm:pt-28 pb-16">{body}</main>
        <PlatformFooter />
      </>
    );
  }
  return <div>{body}</div>;
}

function QuizBody({ slug, quizId }: { slug: string; quizId: number }) {
  const location = useLocation();
  const me = useMe();
  const user = me.data?.user ?? null;

  const quizQ = useQuizForLearner(quizId);
  const quiz = quizQ.data?.quiz;

  const submitAttempt = useSubmitQuizAttempt(quizId);

  const [phase, setPhase] = useState<Phase>("intro");
  const [exercises, setExercises] = useState<GeneratedExercise[]>([]);
  const [result, setResult] = useState<QuizResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const backLink = (
    <Link
      to={`/courses/${slug}`}
      className="inline-flex items-center gap-1.5 font-grotesk text-[0.7rem] tracking-[0.16em] uppercase text-[var(--color-ink-mute)] hover:text-[var(--color-violet)] transition-colors mb-7"
    >
      ← Back to course
    </Link>
  );

  if (quizQ.isLoading) {
    return (
      <div className="container-edit max-w-[760px]">
        <p className="font-grotesk text-[0.9rem] text-[var(--color-ink-mute)]">Loading…</p>
      </div>
    );
  }
  if (!quiz) {
    return (
      <div className="container-edit max-w-[760px]">
        <h2 className="font-display text-[1.85rem] leading-tight mb-4">Quiz not found.</h2>
        <Link to={`/courses/${slug}`} className="row-action">
          <span>← Back to course</span>
        </Link>
      </div>
    );
  }

  // Not logged in → prompt.
  if (!user) {
    return (
      <div className="container-edit max-w-[760px]">
        {backLink}
        <QuizHeader quiz={quiz} />
        <div className="mt-8 border border-[var(--color-rule)] bg-[var(--color-vellum-dim)] rounded-lg p-6">
          <p className="font-display text-[1.2rem] leading-snug mb-3">Log in to practice</p>
          <p className="text-[0.95rem] text-[var(--color-ink-soft)] mb-5">
            Your attempts and score are saved to your account.
          </p>
          <Link
            to={`/login?next=${encodeURIComponent(location.pathname)}`}
            className="course-cta"
          >
            Log in
          </Link>
        </div>
      </div>
    );
  }

  const attemptsLeft =
    quiz.maxAttempts !== null ? Math.max(0, quiz.maxAttempts - quiz.attemptsUsed) : null;
  const attemptsExhausted = quiz.maxAttempts !== null && quiz.attemptsUsed >= quiz.maxAttempts;

  const exerciseCount = countExercises(quiz);

  const startRun = () => {
    setError(null);
    const generated = generateExercises(quiz.content, Math.floor(Math.random() * 0x7fffffff));
    if (generated.length === 0) {
      setError("This exercise set has no items configured yet.");
      return;
    }
    setExercises(generated);
    setResult(null);
    setPhase("running");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFinish = async (r: { correct: number; total: number; score: number }) => {
    setError(null);
    try {
      const submitted = await submitAttempt.mutateAsync({
        score: r.score,
        correct: r.correct,
        total: r.total,
      });
      setResult(submitted.result);
    } catch {
      setError("Something went wrong saving your score. Please try again.");
    }
    setPhase("result");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // -- Running ---------------------------------------------------------------
  if (phase === "running") {
    return (
      <div className="container-edit max-w-[680px]">
        <ExerciseRunner exercises={exercises} onFinish={(r) => void handleFinish(r)} />
      </div>
    );
  }

  // -- Result ----------------------------------------------------------------
  if (phase === "result") {
    const passed = result?.passed ?? false;
    const score = result?.score ?? 0;
    const threshold = result?.passThreshold ?? quiz.passThreshold;
    // attemptsUsed in `quiz` is from before this attempt; account for the one just made.
    const canRetry =
      quiz.maxAttempts === null || quiz.attemptsUsed + 1 < quiz.maxAttempts;

    return (
      <div className="container-edit max-w-[680px]">
        {backLink}
        <QuizHeader quiz={quiz} />

        <div className={`ex-result mt-8 rounded-2xl border p-7 text-center ${passed ? "is-pass" : "is-fail"}`}>
          <p
            className="font-grotesk text-[0.66rem] tracking-[0.2em] uppercase mb-2"
            style={{ color: passed ? "var(--color-correct)" : "var(--color-wrong)" }}
          >
            {passed ? "Passed ✓" : "Keep practicing"}
          </p>
          <p className="font-display text-[3.25rem] leading-none tracking-tight text-[var(--color-ink)]">
            {score}%
          </p>
          <p className="text-[0.95rem] text-[var(--color-ink-soft)] mt-3">
            {passed
              ? `You passed — the pass mark is ${threshold}%.`
              : `You need ${threshold}% to pass. Try again!`}
          </p>
        </div>

        {error && (
          <p className="mt-5 font-grotesk text-[0.85rem] text-[var(--color-violet-deep)]">{error}</p>
        )}

        <div className="mt-8 pt-6 border-t border-[var(--color-rule)] flex flex-wrap items-center gap-3">
          {canRetry ? (
            <button type="button" onClick={startRun} className="course-cta">
              Try again
            </button>
          ) : (
            <span className="font-grotesk text-[0.8rem] text-[var(--color-ink-mute)]">
              No attempts left.
            </span>
          )}
          <Link to={`/courses/${slug}`} className="course-cta-ghost">
            Back to course
          </Link>
        </div>
      </div>
    );
  }

  // -- Intro -----------------------------------------------------------------
  return (
    <div className="container-edit max-w-[680px]">
      {backLink}
      <QuizHeader quiz={quiz} />

      {attemptsExhausted ? (
        <div className="mt-8 border border-[var(--color-rule)] bg-[var(--color-vellum-dim)] rounded-lg p-6">
          <p className="font-display text-[1.2rem] leading-snug mb-2">No attempts left</p>
          <p className="text-[0.95rem] text-[var(--color-ink-soft)]">
            You've used all {quiz.maxAttempts} attempt
            {quiz.maxAttempts === 1 ? "" : "s"} for this set.
            {quiz.bestScore !== null ? ` Your best score was ${quiz.bestScore}%.` : ""}
          </p>
          <Link to={`/courses/${slug}`} className="course-cta-ghost mt-5">
            Back to course
          </Link>
        </div>
      ) : (
        <div className="mt-8 border border-[var(--color-rule)] bg-[var(--color-vellum-dim)] rounded-lg p-6">
          <dl className="flex flex-wrap gap-x-8 gap-y-3 mb-6">
            <div>
              <dt className="font-grotesk text-[0.6rem] tracking-[0.16em] uppercase text-[var(--color-ink-mute)]">
                Exercises
              </dt>
              <dd className="font-display text-[1.4rem] leading-none mt-1">{exerciseCount}</dd>
            </div>
            <div>
              <dt className="font-grotesk text-[0.6rem] tracking-[0.16em] uppercase text-[var(--color-ink-mute)]">
                Pass mark
              </dt>
              <dd className="font-display text-[1.4rem] leading-none mt-1">{quiz.passThreshold}%</dd>
            </div>
            <div>
              <dt className="font-grotesk text-[0.6rem] tracking-[0.16em] uppercase text-[var(--color-ink-mute)]">
                Attempts left
              </dt>
              <dd className="font-display text-[1.4rem] leading-none mt-1">
                {attemptsLeft === null ? "∞" : attemptsLeft}
              </dd>
            </div>
            {quiz.bestScore !== null && (
              <div>
                <dt className="font-grotesk text-[0.6rem] tracking-[0.16em] uppercase text-[var(--color-ink-mute)]">
                  Best score
                </dt>
                <dd className="font-display text-[1.4rem] leading-none mt-1">{quiz.bestScore}%</dd>
              </div>
            )}
          </dl>

          {error && (
            <p className="mb-4 font-grotesk text-[0.85rem] text-[var(--color-violet-deep)]">{error}</p>
          )}

          <button type="button" onClick={startRun} className="course-cta w-full sm:w-auto sm:min-w-[200px]">
            Start practice
            <span aria-hidden>→</span>
          </button>
        </div>
      )}
    </div>
  );
}

/** Total exercises a run will produce (sum of step counts, capped by pool size). */
function countExercises(quiz: PublicQuizForLearner): number {
  const c = quiz.content;
  if (c.version === 3) {
    return c.steps.reduce((sum, s) => sum + (s.type === "match" ? s.count * s.pairCount : s.count), 0);
  }
  return c.steps.reduce((sum, s) => sum + s.count, 0);
}

function QuizHeader({ quiz }: { quiz: PublicQuizForLearner }) {
  const count = useMemo(() => countExercises(quiz), [quiz]);
  return (
    <header>
      <p className="font-grotesk text-[0.62rem] tracking-[0.2em] uppercase text-[var(--color-ink-mute)] mb-2">
        Practice · {count} exercise{count === 1 ? "" : "s"}
      </p>
      <h1 className="font-display text-[clamp(1.7rem,4vw,2.6rem)] leading-[1.05] tracking-[-0.02em]">
        {quiz.title}
      </h1>
      {quiz.description && (
        <p className="text-[1.02rem] leading-[1.6] text-[var(--color-ink-soft)] max-w-[60ch] mt-4">
          {quiz.description}
        </p>
      )}
    </header>
  );
}
